import React, { useState, useEffect } from "react"
import { AppContext, ICheat } from "./appContext";
import { User } from "@firebase/auth";
import { Auth } from "./auth";
import { loadClients, loadClientsRaw, loadSessionsForClient } from "./api/loadClients";
import { putUserSettings, getUserSettings } from "./api/userApi";
import { IClientData, ISession } from "./types/dashboard";
import { fullClients } from "./mocks/clients";
import { getNotes } from "./api/notesApi";
import ru from './translates/ru';
import en from './translates/en';
import ua from './translates/ua';
import { sendFiles } from "./api/api";
import { getSessions, serverToClientSession } from "./api/sessionApi";
import { getUserData } from "./api/api";
import { useModalSystem } from "./utils/useModalSystem";
import { HotkeysModal } from "./components/hotkeysModal/HotkeysModal";
import { interceptConsole } from "./utils/consoleInterceptor";
import { captureScreenshot } from "./utils/screenshot";
import { Environment } from "./utils/environment";
import { clearNavigationState } from "./utils/navigationStatePersistence";
import { formatDate1 } from "./utils/dateFormats";
import { setSessionButtonSizeForLanguage } from "./utils/languageButtonSizes";
import { getUserTimezone as getTimezoneFromUtils } from "./utils/timezone";
import { ConnectionOverlay } from "./components/connectionOverlay/ConnectionOverlay";
import { addConnectionListener, checkConnection } from "./utils/connectionState";

interface IAppControllerProps extends React.PropsWithChildren{

}

export const AppController = ({children}: IAppControllerProps) => {
    const [user, setUser] = useState<User>(null);
    const [clients, setClients] = useState<IClientData[]>();
    const [clientsRaw, setClientsRaw] = useState<IClientData[]>();
    const [sessionsRaw, setSessionsRaw] = useState<Record<string, ISession[]>>({});
    const [progresses, setProgresses] = useState<Record<string, {current:number, total:number, isFailed: boolean}>>({});
    const [fontScaler, setFontScaler] = useState(1);
    const [userSettings, setUserSettings] = useState<any>(null);
    const [currentLanguage, setCurrentLanguage] = useState('en');
    const [wallpaper, _setWallpaper] = useState(localStorage.getItem('wallpaper') || '');
    const [userTimezone, _setUserTimezone] = useState(getTimezoneFromUtils());
    const [isAdmin, setIsAdmin] = useState(false);
    const [cheat, setCheat] = useState<ICheat>({
        debug: false
    });
    const [isGapiReady, setIsGapiReady] = useState(false);
    const [connectionError, setConnectionError] = useState(false);
    const modalSystem = useModalSystem();

    useEffect(() => {
        const unsubscribe = addConnectionListener(setConnectionError);
        return unsubscribe;
    }, []);

    const handleConnectionRetry = async () => {
        return await checkConnection();
    };

    const languages = {
        en: en,
        ru: ru,
        ua: ua
    }

    const translate = (key: string) =>{
        return ((languages[currentLanguage as keyof typeof languages] || en) as any)[key]
            || ((en) as any)[key]
            || key;
    }

    const loadUserSettings = ()=>{
        getUserSettings().then(res=>res.json()).then(userSettings=>{
            setUserSettings(userSettings);
        })
    }

    const saveUserSettings = (data: any)=>{
        return putUserSettings(data).then(res=>{
            return loadUserSettings();
        });
    }

    const loadClients = ()=>{
        loadClientsRaw().then(_clients=> setClientsRaw(_clients));
    }

    const loadAllSessions = ():Promise<Array<ISession>>=>{
        return getSessions().then(res=>res.json()).then(_sessions=>{
            let sessionsArray: any[];
            if (Array.isArray(_sessions)) {
                sessionsArray = _sessions;
            } else if (_sessions && typeof _sessions === 'object') {
                const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
                sessionsArray = Object.entries(_sessions)
                    .filter(([id]) => !reservedKeys.includes(id))
                    .map(([id, data]) => ({ [id]: data }));
            } else {
                sessionsArray = [];
            }
            const sess = sessionsArray.map((it: any)=>{
                const id = Object.keys(it)[0];
                const data = it[id];
                return serverToClientSession({
                    ...data,
                    id
                })
            });
            return sess;
        }
    )}

    const loadSessions = (clientId: string)=>{
        loadSessionsForClient(clientId).then(_sessions=>{
            console.log(sessionsRaw);
            setSessionsRaw(last=> ({...last, [clientId]: _sessions}));

            // Cache the start and end time of the most recent session
            if (_sessions && _sessions.length > 0) {
                const sortedSessions = [..._sessions].sort((a, b) =>
                    new Date(b.startDatetime || b.date).getTime() - new Date(a.startDatetime || a.date).getTime()
                );
                const cachedDate1 = sortedSessions[0]?.startDatetime || sortedSessions[0]?.date || '';
                const cachedDate2 = sortedSessions[0]?.endDatetime || sortedSessions[0]?.dateTo || '';

                const cacheKey = `client_dates_${clientId}`;
                localStorage.setItem(cacheKey, JSON.stringify({
                    date1: cachedDate1,
                    date2: cachedDate2
                }));

                // Update the client data with new cached dates
                setClientsRaw(lastClients => {
                    if (!lastClients) return lastClients;
                    return lastClients.map(client => {
                        if (client.id === clientId) {
                            return {
                                ...client,
                                cachedDate1,
                                cachedDate2
                            };
                        }
                        return client;
                    });
                });
            }

            _sessions.forEach((session: ISession)=>{
                setProgresses(last=>{
                    const next = {...last};
                    if (last[session.id] && last[session.id].current == last[session.id].total){
                        console.log('remove complete progress ', session.id);
                        delete next[session.id];
                    }
                    return next;
                });
            })
        });
    }

    const updateLocalSession = (sessionData: ISession) => {
        const clientId = sessionData.client;
        setSessionsRaw((last) => ({
            ...last,
            [clientId]: last[clientId]?.map(it => it.id === sessionData.id ? sessionData : it) || []
        }));
        // Clear client session cache
        localStorage.removeItem(`cached_client_sessions_${clientId}`);
        localStorage.removeItem(`cached_client_sessions_timestamp_${clientId}`);
    };

    const addLocalSession = (sessionData: ISession) => {
        const clientId = sessionData.client;
        setSessionsRaw((last) => ({
            ...last,
            [clientId]: [...(last[clientId] || []), sessionData]
        }));
        // Clear client session cache
        localStorage.removeItem(`cached_client_sessions_${clientId}`);
        localStorage.removeItem(`cached_client_sessions_timestamp_${clientId}`);
    };

    const removeLocalSession = (sessionId: string, clientId: string) => {
        setSessionsRaw((last) => ({
            ...last,
            [clientId]: last[clientId]?.filter(it => it.id !== sessionId) || []
        }));
        // Clear client session cache
        localStorage.removeItem(`cached_client_sessions_${clientId}`);
        localStorage.removeItem(`cached_client_sessions_timestamp_${clientId}`);
    };

    // Polling logic moved to DashboardController for better navigation awareness

    const uploadSessionFiles = (files: FileList, sessionId: string)=>{
        // Initialize progress immediately when upload starts with 'uploading' stage
        setProgresses(last=>({
            ...last,
            [sessionId]: {current: 0, total: 100, isFailed: false, stage: 'uploading'}
        }));
        
        sendFiles(files, sessionId, (current, total)=>{
            setProgresses(last=>({
                ...last,
                [sessionId]: {current, total, isFailed: false, stage: 'uploading'}
            }));
        }).then(()=>{
            setProgresses(last=>({
                ...last,
                [sessionId]: {
                    current: last[sessionId]?.total || 100,
                    total: last[sessionId]?.total || 100,
                    isFailed: false,
                    stage: 'uploaded'
                }
            }));

            console.log('uploaded ', sessionId)
        }).catch(()=>{
            setProgresses(last=>({
                ...last,
                [sessionId]: {...last[sessionId], isFailed: true, stage: 'failed'}
            }));
            console.log('uploading error ', sessionId)
        }).finally(()=>{
            const processingClientIds: Array<string> = [];
            Object.keys(sessionsRaw).forEach(clientId=>{
                const sessions = sessionsRaw[clientId];
                const processing = sessions.filter(session=> session.id == sessionId);
                if (processing.length){
                    processingClientIds.push(clientId);
                }
            });
            if (processingClientIds[0]){
                console.log('reload sessions for', processingClientIds[0]);
                loadSessions(processingClientIds[0]);
            }
        })
    }

    useEffect(()=>{
        document.body.style.setProperty('--fontScaler', fontScaler.toString());
    }, [fontScaler]);

    useEffect(()=>{
        if (!user){
            console.log('not auth');
            // Clear navigation state when user logs out, but NOT on folder deep link pages
            // (they open in new tabs and shouldn't affect the main app state)
            const isFolderDeepLink = window.location.hash.includes('/folder/');
            if (!isFolderDeepLink) {
                clearNavigationState();
            }
            return;
        }

        // Debug environment detection
        console.log('Environment debug:', {
            hostname: window.location.hostname,
            NODE_ENV: process.env.NODE_ENV,
            isProduction: Environment.isProduction(),
            isDevelopment: Environment.isDevelopment(),
            isReplit: Environment.isReplit(),
            environment: Environment.getEnvironment()
        });

        loadClients();
        loadUserSettings();
        getUserData().then((data: { Admin?: boolean } | null) => {
            setIsAdmin(data?.Admin === true);
        }).catch(() => {
            setIsAdmin(false);
        });
    }, [user]);

    useEffect(()=>{
        if (!clients){
            setClients(last=>clientsRaw);
        }
        if (!clientsRaw){
            return;
        }
        setClients(last=>{
            if (!last) return [];
            const newClients = last.map(client=>{
                if (client.sessions != sessionsRaw[client.id]){
                    const newClient = {...client};
                    newClient.sessions = sessionsRaw[client.id];
                    return newClient;
                }
                return client;
            }).filter((it)=>clientsRaw.find(jt=> jt.id == it.id) != undefined);

            clientsRaw.forEach(it=>{
                const foundClientIndex = newClients.findIndex(jt=> jt.id == it.id);
                if (foundClientIndex == -1){
                    newClients.push(it)
                } else {
                    // Preserve cached dates when updating client data
                    const existingClient = newClients[foundClientIndex];
                    newClients[foundClientIndex] = {
                        ...existingClient,
                        ...it,
                        waitLoading: it.waitLoading ? true : false,
                        cachedDate1: existingClient.cachedDate1 || it.cachedDate1 || '',
                        cachedDate2: existingClient.cachedDate2 || it.cachedDate2 || ''
                    }
                }
            });
            console.log(newClients);
            return newClients;
        });
    }, [clientsRaw, sessionsRaw]);

    useEffect(()=>{
        (window as any).debug = ()=>{
            setCheat((last)=>({
                ...last,
                debug: !last.debug
            }))
        }
    }, []);

    const setWallpaper = (value: string)=>{
        localStorage.setItem('wallpaper', value);
        _setWallpaper(value);
    }

    const setUserTimezone = (timezone: string)=>{
        localStorage.setItem('user_timezone', timezone);
        _setUserTimezone(timezone);
    }

    useEffect(()=>{
        const savedFontScaler = localStorage.getItem('fontScaler');
        if (savedFontScaler){
            setFontScaler(parseFloat(savedFontScaler));
        }
        const savedWallpaper = localStorage.getItem('wallpaper');
        if (savedWallpaper){
            setWallpaper(savedWallpaper);
        }
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage){
            setCurrentLanguage(savedLanguage);
        }
    }, [user]);

    // Apply button sizes whenever language changes
    useEffect(() => {
        setSessionButtonSizeForLanguage(currentLanguage);
    }, [currentLanguage]);

    // Инициализируем перехватчик логов консоли как можно раньше
    useEffect(() => {
        interceptConsole();
    }, []);

    // Global keyboard listener for Ctrl + / report shortcut
    useEffect(() => {
        let isReportModalOpen = false;
        
        const handleGlobalKeydown = async (e: KeyboardEvent) => {
            // Check for Ctrl+/ or Cmd+/ with improved international layout support
            const isShortcut = (e.ctrlKey || e.metaKey) && (
                e.key === '/' || e.key === '?' || 
                e.keyCode === 191 || e.code === 'Slash'
            );
            
            if (!isShortcut) return;
            
            // Prevent duplicate triggers from holding keys
            if (e.repeat) return;
            
            // Skip if modal already open
            if (isReportModalOpen) return;
            
            // Skip if focus is in input fields
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.isContentEditable
            )) return;
            
            e.preventDefault();
            isReportModalOpen = true;
            
            console.log('Opening report modal immediately...');
            
            // Start screenshot capture as a promise - don't wait for it
            const screenshotPromise = captureScreenshot().catch((error) => {
                console.warn('Screenshot capture failed:', error);
                throw error; // Let the modal handle the error
            });
            
            // Open modal immediately with screenshot promise
            try {
                modalSystem.callModal((close) => 
                    <HotkeysModal 
                        close={() => { 
                            console.log('Closing report modal');
                            isReportModalOpen = false; 
                            close(); 
                        }} 
                        screenshotPromise={screenshotPromise}
                    />
                );
                console.log('Report modal opened immediately - screenshot loading in background');
            } catch (modalError) {
                console.error('Failed to open modal system:', modalError);
                // Reset flag in case of modal system failure
                isReportModalOpen = false;
            }
        };

        document.addEventListener('keydown', handleGlobalKeydown);
        
        return () => {
            document.removeEventListener('keydown', handleGlobalKeydown);
        };
    }, []);

    return (
        <AppContext.Provider value={{
            user: user,
            setUser: setUser,
            clients: clients,
            //setClients: setClients,
            //clientsRaw,
            setClientsRaw,
            sessionsRaw,
            setSessionsRaw,
            cheat,
            setCheat,
            fontScaler,
            setFontScaler,

            loadClients,
            loadSessions,
            translate,
            currentLanguage,
            setCurrentLanguage,
            userTimezone,
            setUserTimezone,
            userSettings,
            saveUserSettings,
            progresses,
            uploadSessionFiles,
            wallpaper: wallpaper || './w1.png',
            setWallpaper,
            loadAllSessions,
            isAdmin,
            updateLocalSession,
            addLocalSession,
            removeLocalSession
        }}>
            <Auth></Auth>
            {children}
            {modalSystem.modals}
        </AppContext.Provider>
    )
}